import React, { useContext, useRef } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  ExhibitorCardWrapper,
  ExhibitorCardWrapperMobile,
  StyledChip,
  IconButtonContainerMobile,
  RedLinkButton,
  ElipsedTypography,
  StyledTooltip,
  StyledAddIcon,
  VisitToggleRadio,
  RadioButtonWannaBe,
} from './styles';
import {
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  EditCalendarIconWrapper,
  MailOutlineIconWrapper,
  MapOutlinedIconWrapper,
} from './icons';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { useState } from 'react';
import { useEffect } from 'react';
import { useNetwork } from 'services/network';
import { SdmImageV5 } from './components/SdmImageV5';
import {
  VisitStatusEnum,
  useBookmarkSelector,
  useBookmarkStore,
  useBookmarks,
} from 'pages/exhibitor/exhibitorBookmarks';
import { ExhibitorContext } from 'context/ExhibitorContextProvider';
import {
  Roles,
  isBookingBrandActive,
  isMapBrandActive,
  isPostEvent,
} from 'utils/constants';
import { AppointmentDialog, MapDialog } from './Dialog';
import { LoaderCountContext } from 'context/LoaderV5';
import { FilterContext } from 'context/FilterContextProvider';
import {
  setMapLayerEvent,
  setExhibitorLayerEvent,
} from 'utils/dataLayerManager';

const ExhibitorCard = ({
  exhibitor,
  isOnPersonalPage = false,
  isVisited = false,
  navigateExhibitorDetail,
  openRegisterDialog = null,
  locale,
  loggedUser,
}) => {
  if (!exhibitor) return null;
  const isOperator = loggedUser.type === Roles.OPERATOR;
  const showActions = exhibitor.hall && exhibitor.stand;
  const disableBook =
    (!exhibitor.prenotazioneAppuntamenti && !exhibitor.appuntamenti) ||
    !exhibitor?.salesforceId;
  //hooks
  const mediaTheme = useTheme();
  const isMdUp = useMediaQuery(mediaTheme.breakpoints.up('md'));
  const is376Down = useMediaQuery(mediaTheme.breakpoints.down(376));
  const intl = useIntl();
  const i18nCommon = translations.ExhibitorsV2(intl).Common;
  const networkHelper = useNetwork();
  const linkEmailRef = useRef(null);
  const { setLoaderActive } = useContext(LoaderCountContext);
  const { filter } = useContext(FilterContext);
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);

  //States
  const [itineraries, setItineraries] = useState();
  const [fetchedSrcLogoUrl, setFetchedSrcLogoUrl] = useState();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [mapDialogState, setMapDialogState] = useState({
    isOpen: false,
    thematic: '',
  });
  const closeDialog = () => setIsDialogOpen(false);
  const closeMapDialog = () =>
    setMapDialogState({
      isOpen: false,
      thematic: '',
    });

  // Stores
  const {
    addExhibitorToStore,
    changeExhibitorStatus,
    exhibitorsManage,
    loggedUserId,
    isEditing,
    cardsToManage,
    removeExhibitorFromStore,
    removeExhibitorsFromFolderInStore,
    folders: storeFolders,
  } = useBookmarkSelector();
  const { saveExhibitors, deleteExhibitorsFromFolder } = useBookmarks();
  const { incognitoMode } = useContext(ExhibitorContext);

  //Effects
  useEffect(() => {
    if (exhibitor) {
      fetchLogo();
    }
  }, [exhibitor]);

  useEffect(() => {
    if (filter && filter?.percorsi_tematici) {
      if (exhibitor.percorsiTematici) {
        const itineriesArray = exhibitor.percorsiTematici
          .split('|')
          .map((el) => {
            const trimmed = el.trim();
            return {
              value: trimmed,
              label: filter?.percorsi_tematici?.find(
                (fil) => fil.code === trimmed
              )?.label,
            };
          })
          .filter((el) => !!el.label);
        if (itineriesArray?.length) {
          setItineraries(itineriesArray);
        } else {
          setItineraries([]);
        }
      } else {
        setItineraries([]);
      }
    }
  }, [filter, exhibitor]);

  //utils
  const fetchLogo = async () => {
    if (exhibitor.salesforceId) {
      const url = await networkHelper.getExhibitorLogoUrl(
        exhibitor?.salesforceId
      );
      setFetchedSrcLogoUrl(url);
    } else {
      setFetchedSrcLogoUrl(null);
    }
  };

  const onExhibitorSelection = () => {
    navigateExhibitorDetail && navigateExhibitorDetail(exhibitor);
  };

  const handleContact = (event) => {
    event.stopPropagation();
    linkEmailRef.current.click();
    setExhibitorLayerEvent({
      event: 'contact_exhibitor',
      exhibitorName: exhibitor.name,
    });
  };

  const handleItinerary = (itineraryCode) =>
    setMapDialogState({ isOpen: true, thematic: itineraryCode });

  const mapClickEvent = () => {
    setMapLayerEvent({
      event: 'popup_open',
      popup_name: 'salone_del_mobile_map',
    });
    setMapDialogState({ isOpen: true, thematic: '' });
  };
  const onMailClick = (event) => handleContact(event);
  const onCalendarClick = () => {
    if ((disableBook && !incognitoMode) || isEditing) return null;
    if (incognitoMode) return openRegisterDialog?.('calendar');
    // TODO HANDLE ADD CALENDAR
    setIsDialogOpen(true);
  };
  const onVisit = () => {
    if (incognitoMode) return openRegisterDialog?.('visit');
    addExhibitorToStore({
      exhibitor: { ...exhibitor, status: VisitStatusEnum.TO_VISIT },
    });
    saveExhibitors({
      salesforceId: loggedUserId,
      status: VisitStatusEnum.TO_VISIT,
      exhibitorsIds: [exhibitor.salesforceId],
    });
  };

  const removeFromToVisit = async () => {
    const exInFolder = storeFolders?.find((f) =>
      f.exhibitors.includes(exhibitor?.salesforceId)
    );
    if (exInFolder) {
      await deleteExhibitorsFromFolder({
        useLoader: false,
        salesforceId: loggedUserId,
        folderId: exInFolder.folderId,
        exhibitorsIds: [exhibitor?.salesforceId],
      });
      removeExhibitorsFromFolderInStore({
        folderId: exInFolder.folderId,
        exhibitors: [exhibitor?.salesforceId],
      });
    }
    removeExhibitorFromStore({
      id: exhibitor?.salesforceId,
    });
    saveExhibitors({
      salesforceId: loggedUserId,
      status: VisitStatusEnum.NONE,
      exhibitorsIds: [exhibitor?.salesforceId],
    });
  };

  const isAlreadyInVisit = !!exhibitorsManage?.find(
    (ex) => ex.salesforceId === exhibitor.salesforceId
  );

  const addCardToManage = (currExhibitor) =>
    useBookmarkStore.setState((prev) => ({
      cardsToManage: cardsToManage.find(
        (c) => c.salesforceId === currExhibitor.salesforceId
      )
        ? prev.cardsToManage.filter(
            (ex) => ex.salesforceId !== currExhibitor.salesforceId
          )
        : [...prev.cardsToManage, currExhibitor],
    }));

  const markAsVisited = (currExhibitor, isCurrVisited) => {
    // Only Change between To Visit and Visited
    changeExhibitorStatus({
      exhibitor: currExhibitor,
      newStatus: isCurrVisited
        ? VisitStatusEnum.TO_VISIT
        : VisitStatusEnum.VISITED,
    });
    saveExhibitors({
      salesforceId: loggedUserId,
      status: isCurrVisited
        ? VisitStatusEnum.TO_VISIT
        : VisitStatusEnum.VISITED,
      exhibitorsIds: [exhibitor.salesforceId],
    });
  };

  const closeAlert = (sev) =>
    useBookmarkStore.setState({ alertData: { severity: sev } });

  const showAlert = ({ sev = 'success', title = '' }) =>
    useBookmarkStore.setState({
      alertData: {
        title: title,
        onClose: () => closeAlert(sev),
        severity: sev,
      },
    });

  const onAppointmentSubmit = async (data) => {
    closeDialog();
    let error = true;
    if (!exhibitor.appuntamenti) {
      showLoader();
      const input = {
        userSalesforceId: loggedUser.salId,
        brandName: exhibitor.name,
        brandSalesforceId: exhibitor.salesforceId,
        brandEmail: exhibitor.emailDigitale,
        date: data?.date?.value,
        startTime: data?.morningStart || '9:30',
        slotTime: data?.slotTime?.value || 30,
        language: data?.language?.value,
        phone: data.phone,
        company: data.company,
        reason: data.reason,
        stand: exhibitor.stand || 'N.A.',
        pavillion: exhibitor.hall || 'N.A.',
      };
      const bookingRes = await networkHelper.agendaLambda(
        input,
        '/createProposal'
      );
      hideLoader();
      showAlert({
        sev: !bookingRes ? 'error' : 'success',
        title: !bookingRes
          ? 'Something went wrong, pleade try again later'
          : 'Request sent successfully',
      });
    } else {
      showLoader();
      try {
        const input = {
          brandName: exhibitor.name,
          brandSalesforceId: exhibitor.salesforceId,
          brandEmail: exhibitor.emailDigitale,
          userSalesforceId: loggedUser.salId,
          slotId: data.time.value,
          language: data.language.value,
          phone: data.phone,
          company: data.company,
          reason: data.reason,
          stand: exhibitor.stand || 'N.A.',
          pavillion: exhibitor.hall || 'N.A.',
          type: 'WithDate',
        };
        const res = await networkHelper.agendaLambda(input, '/create');
        error = res.error;
      } catch (error) {
        console.error('LOG::  ~ error', error);
      } finally {
        hideLoader();
        showAlert({
          sev: error ? 'error' : 'success',
          title: error
            ? 'Something went wrong, pleade try again later'
            : 'Request sent successfully',
        });
      }
    }
  };

  return (
    <>
      {!isMdUp ? (
        <ExhibitorCardWrapperMobile
          sx={{
            ...(isEditing &&
              !!cardsToManage.find(
                (c) => c.salesforceId === exhibitor.salesforceId
              ) && {
                border: `1px solid ${mediaTheme.palette.primary.main}`,
              }),
          }}
          onClick={() => {
            if (!isEditing) {
              onExhibitorSelection();
            } else {
              addCardToManage(exhibitor);
            }
          }}
        >
          <Stack
            flexDirection={'row'}
            marginLeft={'auto'}
            width={'215px'}
            gap={'5px'}
          >
            {itineraries?.length ? (
              <>
                {itineraries.map((iti) => (
                  <StyledChip
                    key={iti.value}
                    label={
                      <Typography variant="xs-bold">{iti.label}</Typography>
                    }
                    size="small"
                    disabled={isEditing}
                    style={{ cursor: iti.value ? 'pointer' : 'default' }}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!isEditing) {
                        handleItinerary(iti.value);
                      }
                    }}
                  />
                ))}
              </>
            ) : null}
          </Stack>
          <SdmImageV5
            url={fetchedSrcLogoUrl}
            alt={exhibitor.name || ''}
            styleContainer={{ margin: 'auto' }}
          />
          <ElipsedTypography
            style={{
              fontFamily: 'FSSiena-Regular',
              fontWeight: '600',
              fontSize: '24px',
              lineHeight: '36px',
              width: '100%',
            }}
          >
            {exhibitor.name || ''}
          </ElipsedTypography>
          <Typography
            fontSize={'14px'}
            style={{ fontSize: '14px', color: mediaTheme.palette.grey[900] }}
          >
            {exhibitor.nation || ''}
          </Typography>
          <Divider
            color={mediaTheme.palette.grey[200]}
            style={{ width: '100%' }}
          />
          <Typography
            color={mediaTheme.palette.grey[900]}
            // style={{ fontFamily: 'Suisse-Intl' }}
          >
            {exhibitor.event || ''}{' '}
            {exhibitor.event && (exhibitor.hall || exhibitor.stand)
              ? ` | ${exhibitor.hall || ''} ${exhibitor.stand || ''}`
              : ''}
          </Typography>
          <IconButtonContainerMobile
            onClick={() => {
              if (isEditing) {
                addCardToManage(exhibitor);
              }
            }}
          >
            <Stack
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'flex-start'}
              gap={isAlreadyInVisit && is376Down ? '10px' : '19px'}
            >
              {showActions && isMapBrandActive ? (
                <MapOutlinedIconWrapper
                  customStyle={{
                    ...(isEditing && {
                      color: '#E2001A',
                      // color: '#EC0E00',
                      opacity: 0.2,
                    }),
                  }}
                  mapClickEvent={(event) => {
                    event.stopPropagation();
                    if (!isEditing) {
                      mapClickEvent(exhibitor);
                    } else {
                      addCardToManage(exhibitor);
                    }
                  }}
                />
              ) : null}
              <a
                href={`mailto:${exhibitor?.email}`}
                target="_blank"
                rel="noreferrer"
                hidden
                ref={linkEmailRef}
                onClick={(event) => event.stopPropagation()}
              />
              <MailOutlineIconWrapper
                customStyle={{
                  ...(isEditing && {
                    color: '#E2001A',
                    // color: '#EC0E00',
                    opacity: 0.2,
                  }),
                }}
                mailClickEvent={(event) => {
                  event.stopPropagation();
                  if (!isEditing) {
                    onMailClick(event);
                  } else {
                    addCardToManage(exhibitor);
                  }
                }}
              />
              {isBookingBrandActive &&
              (isOperator || incognitoMode) &&
              showActions ? (
                <EditCalendarIconWrapper
                  customStyle={{
                    ...((isEditing || (disableBook && !incognitoMode)) && {
                      color: '#E2001A',
                      // color: '#EC0E00',
                      opacity: 0.2,
                    }),
                  }}
                  calendarClickEvent={(event) => {
                    event.stopPropagation();
                    if (isEditing) return addCardToManage(exhibitor);
                    if (disableBook && !incognitoMode) return;
                    onCalendarClick(exhibitor);
                  }}
                  hasAppointments={exhibitor.appuntamenti}
                />
              ) : null}
            </Stack>
            {showActions ? (
              <>
                {!isOnPersonalPage ? (
                  <>
                    {!isPostEvent ? (
                      <>
                        {isAlreadyInVisit ? (
                          <RedLinkButton
                            onClick={(event) => {
                              event.stopPropagation();
                              isAlreadyInVisit && removeFromToVisit();
                            }}
                            msg={i18nCommon.addToList}
                            leftIcon={
                              <CheckCircleIcon
                                fontSize="small"
                                color="primary"
                              />
                            }
                            variant="link2"
                            customStyle={{
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                              height: '45px',
                            }}
                          />
                        ) : (
                          <RedLinkButton
                            onClick={(event) => {
                              event.stopPropagation();
                              !isAlreadyInVisit && onVisit();
                            }}
                            msg={i18nCommon.toVisit}
                            leftIcon={<StyledAddIcon color="primary" />}
                            customStyle={{
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                              height: '45px',
                            }}
                          />
                        )}
                      </>
                    ) : null}
                  </>
                ) : (
                  <RadioButtonWannaBe
                    disabled={isEditing}
                    label={
                      isVisited
                        ? i18nCommon.markAsToVisit
                        : i18nCommon.markAsVisited
                    }
                    onClick={() => {
                      if (!isEditing) {
                        markAsVisited(exhibitor, isVisited);
                      } else {
                        addCardToManage(exhibitor);
                      }
                    }}
                  />
                )}
              </>
            ) : null}
          </IconButtonContainerMobile>
          <div
            style={
              isOnPersonalPage
                ? { width: '100%', marginRight: 'auto' }
                : { margin: 'auto' }
            }
          >
            {isOnPersonalPage && showActions ? (
              <>
                {isEditing ? (
                  <Stack width={'100%'} alignItems={'center'}>
                    <VisitToggleRadio
                      style={{ marginRight: '-2px', marginTop: '15px' }}
                      handleChange={(event) => {
                        event.stopPropagation();
                        addCardToManage(exhibitor);
                      }}
                      checked={
                        !!cardsToManage.find(
                          (c) => c.salesforceId === exhibitor.salesforceId
                        )
                      }
                    />
                  </Stack>
                ) : null}
              </>
            ) : (
              <></>
            )}
          </div>
        </ExhibitorCardWrapperMobile>
      ) : (
        <ExhibitorCardWrapper
          sx={{
            ...(isEditing &&
              !!cardsToManage.find(
                (c) => c.salesforceId === exhibitor.salesforceId
              ) && {
                border: `1px solid ${mediaTheme.palette.primary.main}`,
              }),
          }}
          onClick={() => {
            if (!isEditing) {
              onExhibitorSelection();
            } else {
              addCardToManage(exhibitor);
            }
          }}
        >
          {isEditing && showActions ? (
            <VisitToggleRadio
              handleChange={(event) => {
                event.stopPropagation();
                addCardToManage(exhibitor);
              }}
              checked={
                !!cardsToManage.find(
                  (c) => c.salesforceId === exhibitor.salesforceId
                )
              }
            />
          ) : null}
          <Stack
            height={'100%'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <SdmImageV5 url={fetchedSrcLogoUrl} alt={exhibitor.name || ''} />
          </Stack>
          <Divider
            color={mediaTheme.palette.grey[200]}
            orientation="vertical"
            flexItem
          />
          <Stack width={'100%'}>
            <Stack gap={'5px'}>
              <Stack flexDirection={'row'} justifyContent={'space-between'}>
                <ElipsedTypography
                  style={{
                    fontFamily: 'FSSiena-Regular',
                    fontWeight: '600',
                    fontSize: '24px',
                    lineHeight: '36px',
                    maxWidth: '50%',
                  }}
                >
                  {exhibitor.name || ''}
                </ElipsedTypography>
                <Stack
                  flexDirection={'row'}
                  justifyContent={'center'}
                  gap={'25px'}
                >
                  {itineraries?.length ? (
                    <>
                      {itineraries.map((iti) => (
                        <StyledChip
                          key={iti.value}
                          label={
                            <Typography variant="xs-bold">
                              {iti.label}
                            </Typography>
                          }
                          size="small"
                          disabled={isEditing}
                          style={{ cursor: iti.value ? 'pointer' : 'default' }}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (!isEditing) {
                              handleItinerary(iti.value);
                            }
                          }}
                        />
                      ))}
                    </>
                  ) : null}
                </Stack>
              </Stack>
              <Typography
                fontSize={'14px'}
                style={{
                  fontSize: '14px',
                  color: mediaTheme.palette.grey[900],
                }}
              >
                {exhibitor.nation || ''}
              </Typography>
            </Stack>
            <Stack
              flexDirection={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
              gap={'25px'}
            >
              <Typography
                color={mediaTheme.palette.grey[900]}
                style={
                  {
                    // fontFamily: 'Suisse-Intl',
                  }
                }
                variant={'body2'}
              >
                {exhibitor.event || ''}{' '}
                {exhibitor.event && (exhibitor.hall || exhibitor.stand)
                  ? ` | ${exhibitor.hall || ''} ${exhibitor.stand || ''}`
                  : ''}
              </Typography>
              <Stack
                flexDirection={'row'}
                alignItems={'center'}
                justifyContent={'flex-end'}
                gap={'25px'}
              >
                {' '}
                {isOnPersonalPage && showActions ? (
                  <RadioButtonWannaBe
                    disabled={isEditing}
                    label={
                      isVisited
                        ? i18nCommon.markAsToVisit
                        : i18nCommon.markAsVisited
                    }
                    onClick={() => {
                      if (!isEditing) {
                        markAsVisited(exhibitor, isVisited);
                      } else {
                        addCardToManage(exhibitor);
                      }
                    }}
                  />
                ) : (
                  <>
                    {showActions ? (
                      <>
                        {!isPostEvent ? (
                          <>
                            {isAlreadyInVisit ? (
                              <RedLinkButton
                                onClick={(event) => {
                                  event.stopPropagation();
                                  isAlreadyInVisit && removeFromToVisit();
                                }}
                                msg={i18nCommon.addToList}
                                leftIcon={
                                  <CheckCircleIcon
                                    fontSize="small"
                                    color="primary"
                                  />
                                }
                                variant="link2"
                                customStyle={{
                                  justifyContent: 'flex-end',
                                  alignItems: 'center',
                                  height: '36px',
                                }}
                              />
                            ) : (
                              <RedLinkButton
                                onClick={(event) => {
                                  event.stopPropagation();
                                  !isAlreadyInVisit && onVisit();
                                }}
                                msg={i18nCommon.toVisit}
                                leftIcon={
                                  <StyledAddIcon
                                    fontSize="small"
                                    color="primary"
                                  />
                                }
                                variant="link2"
                                customStyle={{
                                  justifyContent: 'flex-end',
                                  alignItems: 'center',
                                  height: '36px',
                                }}
                              />
                            )}
                          </>
                        ) : null}
                      </>
                    ) : null}
                  </>
                )}
                {showActions && isMapBrandActive ? (
                  <StyledTooltip
                    title={i18nCommon.tooltips.map}
                    arrow
                    placement="top"
                  >
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        mapClickEvent(exhibitor);
                      }}
                      disabled={isEditing}
                      sx={{
                        ...(isEditing && {
                          color: '#E2001A',
                          // color: '#EC0E00',
                          opacity: 0.2,
                        }),
                      }}
                    >
                      <MapOutlinedIconWrapper />
                    </IconButton>
                  </StyledTooltip>
                ) : null}
                <a
                  href={`mailto:${exhibitor?.email}`}
                  target="_blank"
                  rel="noreferrer"
                  hidden
                  ref={linkEmailRef}
                  onClick={(event) => event.stopPropagation()}
                />
                <StyledTooltip
                  title={i18nCommon.tooltips.mail}
                  arrow
                  placement="top"
                >
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      onMailClick(event);
                    }}
                    disabled={isEditing}
                    sx={{
                      ...(isEditing && {
                        color: '#E2001A',
                        // color: '#EC0E00',
                        opacity: 0.2,
                      }),
                    }}
                  >
                    <MailOutlineIconWrapper />
                  </IconButton>
                </StyledTooltip>
                {isBookingBrandActive &&
                (isOperator || incognitoMode) &&
                showActions ? (
                  <StyledTooltip
                    title={
                      exhibitor.appuntamenti
                        ? i18nCommon.tooltips.calendar
                        : disableBook && !incognitoMode
                        ? i18nCommon.tooltips.disableBook
                        : i18nCommon.tooltips.calendarEmpty
                    }
                    arrow
                    placement="top"
                  >
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        onCalendarClick(exhibitor);
                      }}
                      disabled={isEditing}
                      sx={{
                        ...((isEditing || (disableBook && !incognitoMode)) && {
                          color: '#E2001A',
                          // color: '#EC0E00',
                          opacity: 0.2,
                          cursor: 'default',
                        }),
                      }}
                    >
                      <EditCalendarIconWrapper
                        hasAppointments={exhibitor.appuntamenti}
                      />
                    </IconButton>
                  </StyledTooltip>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        </ExhibitorCardWrapper>
      )}
      <AppointmentDialog
        isOpen={isDialogOpen}
        onClose={() => closeDialog()}
        exhibitor={exhibitor}
        loggedUser={loggedUser}
        onSubmit={onAppointmentSubmit}
      />
      <MapDialog
        isOpen={mapDialogState.isOpen}
        onClose={() => closeMapDialog()}
        exhibitor={exhibitor}
        locale={locale}
        thematic={mapDialogState.thematic}
        token={loggedUser ? '&token=' + loggedUser.tk : ''}
      />
    </>
  );
};

export { ExhibitorCard };
